import { useState } from 'react';

import { useApiMutation } from '@/api/mutations';
import { Field, textAreaClass } from '@/components/Field';
import { Button, Card, Notice, PageHeading, SectionTitle } from '@/components/PageHeading';
import { undoStockImport, type StockImport } from '@core/api/stockImport';
import { can, canAct } from '@core/session/capability';
import type { Session } from '@core/session/types';
import type { Sheet } from '@core/units/stockImport';

import { TagPrint } from '../TagPrint';
import { sendPhotos, type RunProgress, type RunResult } from './importRun';
import { saveWorkbook } from './workbook';

/** The run, as it goes: designs, then garments, then photos. Nothing to press — it is working. */
export function ImportRunning({ progress }: { progress: RunProgress | null }) {
  const stage = progress?.stage ?? 'starting';
  const words: Record<RunProgress['stage'], string> = {
    starting: 'Starting the import…',
    values: 'Adding the new sizes and colours…',
    designs: 'Making the new designs…',
    garments: 'Booking the garments in…',
    photos: 'Sending the photos…',
    done: 'Finishing…',
  };

  return (
    <div className="flex flex-col gap-3 lg:gap-4">
      <PageHeading eyebrow="Importing — keep this page open" title={words[stage]} />
      <Card>
        <div className="flex flex-col gap-4">
          <Bar
            label="Designs"
            done={progress?.designs.done ?? 0}
            total={progress?.designs.total ?? 0}
          />
          <Bar
            label="Garments booked in"
            done={progress?.garments.done ?? 0}
            total={progress?.garments.total ?? 0}
          />
          <Bar
            label="Photos sent"
            done={progress?.photos.done ?? 0}
            total={progress?.photos.total ?? 0}
          />
        </div>
      </Card>
    </div>
  );
}

function Bar({ label, done, total }: { label: string; done: number; total: number }) {
  const share = total === 0 ? 1 : Math.min(1, done / total);
  const complete = done >= total;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex justify-between text-sm text-gray-900">
        <span>{label}</span>
        <b className={complete ? 'text-success' : 'text-warning'}>
          {total === 0 ? 'None' : `${done} of ${total}${complete ? ' ✓' : ''}`}
        </b>
      </div>
      <span className="bg-light block h-2 overflow-hidden rounded">
        <span
          className={`block h-full rounded ${complete ? 'bg-success' : 'bg-primary'}`}
          style={{ width: `${share * 100}%` }}
        />
      </span>
    </div>
  );
}

/**
 * What came in: the counts, the tags, the sheet back with its new codes, and Undo while nothing
 * has happened to the stock. A run that stopped says where and why — what came in before it stays
 * in, and the same sheet imported again carries on.
 */
export function ImportDone({
  session,
  result,
  sheet,
  fileName,
  onAgain,
  onClose,
}: {
  session: Session;
  result: RunResult;
  sheet: Sheet;
  fileName: string;
  onAgain: () => void;
  onClose: () => void;
}) {
  const [photoFailures, setPhotoFailures] = useState(result.photoFailures);
  const [resending, setResending] = useState(false);
  const [undone, setUndone] = useState(false);

  const units = result.batches.flatMap((batch) => batch.lines.flatMap((line) => line.units));
  const receipts = [
    ...new Set(
      result.batches
        .map((batch) => batch.goodsReceiptNumber)
        .filter((number): number is string => number !== null),
    ),
  ];
  const designsMade = Object.keys(result.designIds).length;
  const photosPlanned = result.plan.newDesigns.filter(
    (design) => design.picture !== null && !design.picture.startsWith('url:'),
  ).length;
  const left = result.plan.rows.filter((row) => row.status === 'out');

  // Each sheet row's new codes, from the batch that booked it: lines come back in the rows' order.
  const codesByRow = new Map<number, string[]>();

  result.batches.forEach((batch, batchIndex) => {
    batch.lines.forEach((line, lineIndex) => {
      const number = result.batchRows[batchIndex]?.[lineIndex];

      if (number !== undefined) {
        codesByRow.set(
          number,
          line.units.map((unit) => unit.serialCode),
        );
      }
    });
  });

  if (undone) {
    return (
      <div className="flex flex-col gap-3 lg:gap-4">
        <PageHeading
          eyebrow={fileName}
          title="Import undone"
          actions={
            <Button variant="primary" shortcut="back" onClick={onClose}>
              Done
            </Button>
          }
        />
        <Notice tone="success" title="Taken back">
          {`The ${units.length} garments and ${designsMade} new designs are gone. The sheet's rows are free — import it again whenever it is ready.`}
        </Notice>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 lg:gap-4">
      <PageHeading
        eyebrow={
          [fileName, ...receipts.slice(0, 3)].join(' · ') +
          (receipts.length > 3 ? ` +${receipts.length - 3}` : '')
        }
        title={`${units.length} garment${units.length === 1 ? '' : 's'} in`}
        subtitle={`${designsMade} new design${designsMade === 1 ? '' : 's'} · ${photosPlanned - photoFailures.length} of ${photosPlanned} photos sent`}
        actions={
          <>
            <Button onClick={onAgain}>Import another sheet</Button>
            <Button variant="primary" shortcut="back" onClick={onClose}>
              Done
            </Button>
          </>
        }
      />

      {result.stopped && (
        <Notice tone="danger" title="The import stopped">
          {`${result.stopped} What came in before this is kept. Put it right and import the same sheet again — rows already in are skipped, so it carries on from here.`}
        </Notice>
      )}

      <div className="grid grid-cols-12 gap-3 lg:gap-4">
        <div className="col-span-12 flex flex-col gap-3 lg:gap-4 xl:col-span-7">
          <Card>
            <SectionTitle>How it went</SectionTitle>
            <div className="flex flex-col gap-4">
              <Bar label="New designs" done={designsMade} total={result.plan.newDesigns.length} />
              <Bar
                label="Garments booked in"
                done={units.length}
                total={result.plan.summary.garments}
              />
              <Bar
                label="Photos sent"
                done={photosPlanned - photoFailures.length}
                total={photosPlanned}
              />
            </div>
            {photoFailures.length > 0 && (
              <div className="bg-warning-transparent mt-4 flex flex-wrap items-center justify-between gap-2 rounded-md p-3">
                <span className="text-[13px] text-gray-900">
                  {`${photoFailures.length} photo${photoFailures.length === 1 ? '' : 's'} did not send. The stock is in; send them again, or add them from each design.`}
                </span>
                <Button
                  disabled={resending}
                  onClick={() => {
                    setResending(true);
                    void sendPhotos(photoFailures).then((failed) => {
                      setPhotoFailures(failed);
                      setResending(false);
                    });
                  }}
                >
                  {resending ? 'Sending…' : `Send the ${photoFailures.length} again`}
                </Button>
              </div>
            )}
            <p className="text-default mt-3 mb-0 text-[13px]">
              Garments go in batches of a hundred, each all or nothing — a lost connection stops the
              import at a batch edge, and running the sheet again carries on from there.
            </p>
          </Card>

          {units.length > 0 && can(session, 'ItemUnit:Print') && (
            <TagPrint session={session} units={units} />
          )}
        </div>

        <div className="col-span-12 flex flex-col gap-3 lg:gap-4 xl:col-span-5">
          <Card>
            <SectionTitle>Next</SectionTitle>
            <div className="flex flex-col items-start gap-2">
              {units.length > 0 && (
                <>
                  <button
                    type="button"
                    className="text-primary cursor-pointer p-0 text-sm font-semibold"
                    onClick={() => void downloadWithCodes(sheet, fileName, codesByRow)}
                  >
                    Download the sheet with the new codes
                  </button>
                  <span className="text-default text-[13px]">
                    Your sheet, each row with its garments’ codes added — the shop’s own record of
                    what came in.
                  </span>
                </>
              )}
              {left.length > 0 && (
                <button
                  type="button"
                  className="text-primary border-border-color w-full cursor-pointer border-t p-0 pt-2 text-start text-sm font-semibold"
                  onClick={() =>
                    void saveWorkbook(
                      'rows-left-out.xlsx',
                      'Left out',
                      ['Row', ...sheet.headers, 'Why it was left out'],
                      left.map((row) => [
                        String(row.number),
                        ...(sheet.rows.find((candidate) => candidate.number === row.number)
                          ?.cells ?? []),
                        row.reasons.join('; '),
                      ]),
                    )
                  }
                >
                  {`Download the ${left.length} row${left.length === 1 ? '' : 's'} left out`}
                </button>
              )}
              <button
                type="button"
                className="text-primary cursor-pointer p-0 text-sm font-semibold"
                onClick={onClose}
              >
                See the garments in Item units
              </button>
            </div>
          </Card>

          {result.importRecord && units.length > 0 && canAct(session, 'ItemUnit:Delete') && (
            <Card>
              <SectionTitle>Undo this import</SectionTitle>
              <p className="text-default mb-3 text-[13px]">
                {`Removes these ${units.length} garments and the ${designsMade} new designs, while none of them has been booked, held, moved or billed. Rents the sheet set on designs already in the catalogue stay as set. Once a garment has been out, take garments out one by one instead.`}
              </p>
              <UndoImport record={result.importRecord} onUndone={() => setUndone(true)} />
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

/** Undo, with its compulsory reason — the server refuses it once anything has happened to the stock. */
export function UndoImport({
  record,
  compact = false,
  onUndone,
}: {
  record: StockImport;
  compact?: boolean;
  onUndone?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');

  const undo = useApiMutation<StockImport, string>({
    mutationFn: (why) => undoStockImport(record.id, why),
    success: 'Import undone.',
    invalidates: [['stock-import'], ['item-unit'], ['item'], ['goods-receipt'], ['item-category']],
    onSuccess: () => {
      setOpen(false);
      onUndone?.();
    },
  });

  if (!open) {
    return (
      <Button
        variant="danger"
        className={compact ? 'text-[12px]' : ''}
        onClick={() => setOpen(true)}
      >
        Undo import…
      </Button>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <Field label="Why is it being undone?" required>
        <textarea
          rows={2}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Wrong sheet, wrong branch…"
          className={textAreaClass}
        />
      </Field>
      <div className="flex gap-2">
        <Button
          variant="danger"
          disabled={reason.trim() === '' || undo.isPending}
          onClick={() => undo.mutate(reason.trim())}
        >
          {undo.isPending ? 'Undoing…' : 'Undo the import'}
        </Button>
        <Button disabled={undo.isPending} onClick={() => setOpen(false)}>
          Keep it
        </Button>
      </div>
    </div>
  );
}

function downloadWithCodes(sheet: Sheet, fileName: string, codesByRow: Map<number, string[]>) {
  return saveWorkbook(
    fileName.replace(/\.(xlsx|csv)$/i, '') + '-with-codes.xlsx',
    'Stock',
    [...sheet.headers, 'Garment codes'],
    sheet.rows.map((row) => [...row.cells, (codesByRow.get(row.number) ?? []).join(', ')]),
  );
}
